import { z } from "zod";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import type { ExternalSystemChoice } from "@components/accountingMigration/source";

type SubView = "picker" | "full" | "glOnly";

export const historicalDataSchema = z.object({
    migrate: z.boolean(),
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
    syncGeneralLedger: z.boolean(),
    syncCounterparties: z.boolean(),
    syncAttachments: z.boolean(),
    syncClasses: z.boolean(),
    syncCatalogItems: z.boolean(),
    syncInvoices: z.boolean(),
    syncBills: z.boolean(),
});

export type HistoricalDataValues = z.infer<typeof historicalDataSchema>;

export type HistoricalDataSource =
    | { type: "full" }
    | { type: "glOnly" }
    | { type: "picker"; selectedSystem: ExternalSystemChoice | null };

export function isMigrationDateRangeValid({
    startDate,
    endDate,
}: {
    startDate: string | undefined;
    endDate: string | undefined;
}): boolean {
    if (!endDate) {
        return false;
    }
    return !startDate || startDate <= endDate;
}

export function getSubView(source: HistoricalDataSource): SubView {
    if (source.type === "full") {
        return "full";
    }
    if (source.type === "glOnly") {
        return "glOnly";
    }
    if (!source.selectedSystem) {
        return "picker";
    }
    return source.selectedSystem ===
        AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE
        ? "full"
        : "glOnly";
}

export function getHistoricalDataSource({
    isQboConnected,
    isQbdJsonMigration,
    isNonQboMigration,
    selectedSystem,
}: {
    isQboConnected: boolean;
    isQbdJsonMigration: boolean;
    isNonQboMigration: boolean;
    selectedSystem: ExternalSystemChoice | null;
}): HistoricalDataSource {
    if (isQboConnected || isQbdJsonMigration) {
        return { type: "full" };
    }
    if (isNonQboMigration) {
        return { type: "glOnly" };
    }
    return { type: "picker", selectedSystem };
}
